<template>
    <div class="customer-v3-container">
        <!-- Stats Header Cards -->
        <CustomerStatsNavigation
            :stats="stats"
            :activeTab="activeTab"
            @click="setActiveTab"
            :token="token"
            :permissions="permissions"
        />

        <!-- Main Content Card -->
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <Header :title="tabTitle" />

            <!-- Loading State -->
            <div v-if="isLoading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent"
                ></div>
            </div>

            <!-- Tab Content -->
            <component
                v-else
                :is="currentTabComponent"
                :key="activeTab"
                :user-id="userId"
                :token="token"
                :is-super-admin="isSuperAdmin"
                @refresh-stats="fetchStats"
                :plan-pro="planPro"
                :tenant="tenant"
            />
        </div>
    </div>
</template>

<script>
import CustomerTable from './CustomerTable.vue'
import CustomerStatsNavigation from './CustomerStatsNavigation.vue'
import axios from 'axios'
import GroupTable from './Group/GroupTable.vue'
import VoucherTable from './Voucher/VoucherTable.vue'
import normalizeSize from '../../../utils/normalizeSize'
import BilletNonDateTable from './BilletNonDate/BilletNonDateTable.vue'
import TreatmentCustomerTable from './Treatment/TreatmentCustomerTable.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import Header from '../../Shared/Header.vue'
export default {
    name: 'BoCustomerV3Index',
    components: {
        CustomerTable,
        GroupTable,
        CustomerStatsNavigation,
        VoucherTable,
        BilletNonDateTable,
        TreatmentCustomerTable,
        Header,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        initialTab: {
            type: String,
            default: 'customers',
        },
        token: {
            type: String,
            required: true,
        },
        permissions: {
            type: Object,
            default: () => ({
                customers: true,
                groups: true,
                subscriptions: true,
                vouchers: true,
                billetsNonDatees: true,
            }),
        },
        planPro: {
            type: Object,
            default: () => ({}),
        },
        tenant: {
            type: Object,
            default: () => ({}),
        },
        isSuperAdmin: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeTab: this.initialTab,
            isLoading: true,
            stats: {
                customers: 0,
                groups: 0,
                newsletter_subscribers: 0,
                subscriptions: 0,
                treatments: 0,
                vouchers: 0,
                subscriptions_treatments: 0,
            },
        }
    },
    computed: {
        tabTitle() {
            const titles = {
                customers: this.$t('customers.title'),
                groups: this.$t('groups.title'),
                billetsNonDatees: this.$t('billetNonDate.title'),
                vouchers: this.$t('voucher.title'),
                subscriptions: this.$t('treatment_customer.title'),
            }
            return titles[this.activeTab] || this.$t('customers.title')
        },
        currentTabComponent() {
            const components = {
                customers: 'CustomerTable',
                // Future tabs can be added here
                groups: 'GroupTable',
                billetsNonDatees: 'BilletNonDateTable',
                subscriptions: 'TreatmentCustomerTable',
                vouchers: 'VoucherTable',
            }
            return components[this.activeTab] || 'CustomerTable'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        setActiveTab(tab) {
            this.activeTab = tab
            // Update URL query parameter without reloading
            const url = new URL(window.location.href)
            url.searchParams.set('tab', tab)
            window.history.pushState({}, '', url.toString())
        },
        async fetchStats() {
            try {
                const response = await axios.get('/api/v3/customer/stats', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.stats = response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
    },
    mounted() {
        // Initialize from URL if present, otherwise use initialTab prop
        const urlParams = new URLSearchParams(window.location.search)
        const tab = urlParams.get('tab')
        const validTabs = ['customers', 'groups', 'billetsNonDatees', 'subscriptions', 'vouchers']

        if (tab && validTabs.includes(tab)) {
            this.activeTab = tab
        } else if (this.initialTab && validTabs.includes(this.initialTab)) {
            this.activeTab = this.initialTab
        }

        this.fetchStats()
        // Remove white background from parent container for V3 layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('customer-v3-no-bg')
        }
    },
    beforeDestroy() {
        // Restore background when leaving the page
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('customer-v3-no-bg')
        }
    },
}
</script>

<style scoped lang="scss">
.customer-v3-container {
    padding: 0;
}
#main-container.customer-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

#main-container:has(.customer-v3-container) {
    background: transparent !important;
}

.dark #main-container.customer-v3-no-bg,
.dark #main-container:has(.customer-v3-container) {
    background: transparent !important;
}
</style>
